/**
 * useCredits - saldo kredytów jako JEDNO wspólne źródło prawdy (React Query cache
 * pod kluczem ["credits"]).
 *
 * Wcześniej hook trzymał saldo w lokalnym useState, więc KAŻDE wywołanie (Topbar,
 * ekran narzędzia, strona oferty…) miało własną, niezależną kopię salda. Instancje
 * się rozjeżdżały: nagłówek pokazywał 73, a generowanie widziało 0 → „Brakuje 3
 * kredytów" mimo dodatniego salda w liczniku. Teraz wszystkie instancje czytają ten
 * sam wpis cache, a optymistyczny delta / realtime / refetch aktualizują go globalnie.
 *
 * Public API (bez zmian):
 *   - balance: number | null (null podczas loading)
 *   - corrections, balanceId, isPaid, subscriptionTier, agency*
 *   - isLoading, error
 *   - refetch: () => void
 *   - hasEnoughCredits: (required: number) => boolean
 *   - applyOptimisticDelta: (delta: number) => void
 */
import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getCredits, type GetCreditsResponse } from "@/lib/apiClient";
import type { PlanTier } from "@adresflow-v2/shared";

export interface UseCreditsResult {
  balance: number | null;
  corrections: number;
  balanceId: string | null;
  isPaid: boolean;
  subscriptionTier: PlanTier;
  agencyId: string | null;
  agencyName: string | null;
  agencyRole: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  hasEnoughCredits: (required: number) => boolean;
  applyOptimisticDelta: (delta: number) => void;
}

const CREDITS_KEY = ["credits"] as const;

// Realtime na credit_balances trzymamy jako JEDEN kanał na całą aplikację
// (moduł-singleton), niezależnie od liczby instancji hooka — inaczej każdy mount
// otwierał osobny channel. Kanał żyje przez sesję i pisze wprost do cache React Query.
let sharedChannel: RealtimeChannel | null = null;
let sharedChannelBalanceId: string | null = null;
// Trailing debounce inwalidacji: w biurze każdy UPDATE wspólnego wiersza salda
// dociera do WSZYSTKICH otwartych aplikacji członków — bez zbicia serii
// generowań w jedno odświeżenie byłby to sztorm refetchy /v1/credits.
let invalidateTimer: ReturnType<typeof setTimeout> | null = null;

export function useCredits(): UseCreditsResult {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<GetCreditsResponse>({
    queryKey: CREDITS_KEY,
    queryFn: async () => {
      const res = await getCredits();
      if ("error" in res) throw new Error(res.error);
      return res.data!;
    },
    enabled: isAuthenticated && !!user,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const data = query.data;
  const balance = data ? data.balance : null;
  const balanceId = data?.balanceId ?? null;

  // Realtime — jeden wspólny kanał; aktualizuje cache, więc wszystkie instancje odświeżą się razem.
  const qcRef = useRef(queryClient);
  qcRef.current = queryClient;
  useEffect(() => {
    if (!balanceId) return;
    if (sharedChannel && sharedChannelBalanceId === balanceId) return; // już aktywny na ten sam balance

    if (sharedChannel) {
      supabase.removeChannel(sharedChannel);
      sharedChannel = null;
      sharedChannelBalanceId = null;
    }

    const channel = supabase.channel(`credit_balance_${balanceId}`);
    channel.on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "credit_balances", filter: `id=eq.${balanceId}` },
      () => {
        // Payload NIE nadaje się na źródło liczby: replikacja nie niesie kolumny
        // GENEROWANEJ `total_balance`, a `payload.new.balance` to martwa legacy
        // kolumna (najczęściej DEFAULT) — jej wpisanie do cache nadpisywało
        // poprawne saldo śmieciem po każdej dedukcji. Realtime jest tylko
        // SYGNAŁEM zmiany — liczbę zawsze dociąga GET /v1/credits.
        if (invalidateTimer) clearTimeout(invalidateTimer);
        invalidateTimer = setTimeout(() => {
          invalidateTimer = null;
          void qcRef.current.invalidateQueries({ queryKey: CREDITS_KEY });
        }, 400);
      },
    );
    channel.subscribe();
    sharedChannel = channel;
    sharedChannelBalanceId = balanceId;
  }, [balanceId]);

  const hasEnoughCredits = useCallback(
    (required: number): boolean => balance !== null && balance >= required,
    [balance],
  );

  // Optymistyczny delta pisze do WSPÓLNEGO cache — natychmiast widoczny wszędzie
  // (licznik w nagłówku + ekran narzędzia), bez rozjazdu instancji.
  const applyOptimisticDelta = useCallback(
    (delta: number) => {
      queryClient.setQueryData<GetCreditsResponse>(CREDITS_KEY, (old) =>
        old ? { ...old, balance: Math.max(0, old.balance + delta) } : old,
      );
    },
    [queryClient],
  );

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: CREDITS_KEY });
  }, [queryClient]);

  return {
    balance,
    corrections: data?.corrections ?? 0,
    balanceId,
    isPaid: data?.isPaid ?? false,
    subscriptionTier: (data?.subscriptionTier as PlanTier | undefined) ?? "free",
    agencyId: data?.agencyId ?? null,
    agencyName: data?.agencyName ?? null,
    agencyRole: data?.agencyRole ?? null,
    isLoading: query.isLoading && isAuthenticated,
    error: query.error ? (query.error as Error).message : null,
    refetch,
    hasEnoughCredits,
    applyOptimisticDelta,
  };
}
